import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, FileText, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import { markShot, rewriteScriptOnShoot, type ShootCreative } from '@/lib/queries';
import { cn } from '@/lib/cn';
import { bidi, BIDI_CLS } from '@/lib/bidi';

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not save';

/**
 * The shoot day, the way the crew works it: one bucket per doctor, each holding that
 * doctor's scripts. Every piece can be read, marked filmed, and corrected on the spot
 * when the doctor says something other than the approved words.
 */
export default function ShootDayList({ shootId, creatives, canRemove, onRemove }: {
  shootId: string;
  creatives: ShootCreative[];
  canRemove?: boolean;
  onRemove?: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<ShootCreative | null>(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['shoot', shootId] });
    qc.invalidateQueries({ queryKey: ['shoots'] });
    qc.invalidateQueries({ queryKey: ['track'] });
  };

  const shot = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => markShot(id, on),
    onSuccess: (_d, v) => { toast.success(v.on ? 'Marked as filmed' : 'No longer marked as filmed'); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rewrite = useMutation({
    mutationFn: ({ id, script, note: n }: { id: string; script: string; note: string }) => rewriteScriptOnShoot(id, script, n),
    onSuccess: () => { toast.success('Saved what the doctor actually said. The content creator has been told.'); setEditing(null); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  // One bucket per doctor, in the order the doctors appear on the day.
  const buckets = useMemo(() => {
    const m = new Map<string, ShootCreative[]>();
    for (const c of creatives) {
      const k = c.talent_name || 'No doctor picked yet';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(c);
    }
    return Array.from(m.entries());
  }, [creatives]);

  if (creatives.length === 0) return null;

  return (
    <div className="space-y-4">
      {buckets.map(([doctor, list]) => {
        const filmed = list.filter((c) => c.shot_at).length;
        return (
          <div key={doctor}>
            <div className="flex items-baseline gap-2 mb-1.5">
              <h4 className="text-sm font-medium text-muted-strong">{doctor}</h4>
              <span className="text-[11px] text-muted">
                {list.length} script{list.length === 1 ? '' : 's'}
                {filmed ? ` · ${filmed} filmed` : ''}
              </span>
            </div>
            <div className="rounded-xl border border-slate-800 divide-y divide-slate-800/60">
              {list.map((c) => (
                <div key={c.id} className="p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Link to={`/creatives/${c.id}#shoot`} className="font-mono text-xs text-accent-400 hover:underline">{c.code}</Link>
                        <span className="text-sm text-muted-strong">{c.title}</span>
                        <span className="text-[10px] uppercase text-muted">{c.creative_type}</span>
                        {c.shot_at && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">
                            filmed{c.shot_by_name ? ` by ${c.shot_by_name}` : ''}
                          </span>
                        )}
                        {c.script_changed_at && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 inline-flex items-center gap-1">
                            <AlertTriangle size={10} /> script changed on set
                          </span>
                        )}
                        {!c.shot_at && (c.raw_count ?? 0) > 0 && (
                          <span className="text-[10px] text-muted">{c.raw_count} raw{c.raw_count === 1 ? '' : 's'} in</span>
                        )}
                      </div>
                      {c.script && (
                        <button type="button" onClick={() => setOpen(open === c.id ? null : c.id)}
                          className="mt-1 text-[11px] text-muted hover:text-muted-strong inline-flex items-center gap-1">
                          <FileText size={11} /> {open === c.id ? 'Hide the script' : 'Read the script'}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant={c.shot_at ? 'secondary' : 'primary'} icon={<Check size={12} />}
                        loading={shot.isPending} onClick={() => shot.mutate({ id: c.id, on: !c.shot_at })}>
                        {c.shot_at ? 'Filmed' : 'Mark filmed'}
                      </Button>
                      <Button size="sm" variant="ghost" icon={<AlertTriangle size={12} />}
                        onClick={() => { setEditing(c); setDraft(c.script ?? ''); setNote(''); }}
                        title="The doctor said something other than the approved script">
                        Script was different
                      </Button>
                      {canRemove && onRemove && (
                        <Button size="sm" variant="ghost" icon={<X size={12} />} onClick={() => onRemove(c.id)}>Remove</Button>
                      )}
                    </div>
                  </div>
                  {open === c.id && c.script && (
                    <pre dir="auto" className={`mt-2 whitespace-pre-wrap text-[12.5px] leading-relaxed text-muted-strong bg-bg-input rounded-lg p-3 border border-slate-800 ${BIDI_CLS}`}>
                      {bidi(c.script)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setEditing(null)}>
          <div className="bg-bg-card border border-slate-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-800">
              <h3 className="font-medium text-muted-strong inline-flex items-center gap-2"><Camera size={15} /> What the doctor actually said</h3>
              <p className="text-xs text-muted mt-1">
                {editing.code} · {editing.title}. Put in the words that are on the tape. The approved version is
                kept, the change is recorded as coming from the doctor, and the content creator and the art
                director are told.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14}
                dir="auto"
                className="w-full bg-bg-input border border-slate-700 rounded-lg p-3 text-sm text-muted-strong focus:outline-none focus:border-accent-400/60"
                placeholder="What the doctor said, as filmed" />
              <input value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full h-10 bg-bg-input border border-slate-700 rounded-lg px-3 text-sm text-muted-strong focus:outline-none focus:border-accent-400/60"
                placeholder="One line on what changed, optional" />
            </div>
            <div className="p-4 border-t border-slate-800 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button icon={<Check size={13} />} loading={rewrite.isPending}
                disabled={!draft.trim() || draft.trim() === (editing.script ?? '').trim()}
                onClick={() => rewrite.mutate({ id: editing.id, script: draft, note })}>
                Save what was said
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
